/**
 * SHAP Force Plot Component
 * 
 * Interactive visualization showing forces pushing prediction higher/lower.
 * Red = pushing higher, Blue = pushing lower
 * 
 * Following BRAIN-DESIGN-SYSTEM.md:
 * - Green (#22C55E) for positive contributions
 * - Red (#EF4444) for negative contributions
 * - Uses Lucide React icons
 */

import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ArrowUp, ArrowDown } from 'lucide-react';

interface ForcePlotProps {
  baseValue: number;
  features: Array<{
    name: string;
    contribution: number;
  }>;
  finalPrediction: number;
}

export const ForcePlot: React.FC<ForcePlotProps> = ({
  baseValue,
  features,
  finalPrediction
}) => {
  const { positiveFeatures, negativeFeatures } = useMemo(() => {
    const positive = features
      .filter(f => f.contribution > 0)
      .sort((a, b) => b.contribution - a.contribution);
    const negative = features
      .filter(f => f.contribution < 0)
      .sort((a, b) => a.contribution - b.contribution);
    return { positiveFeatures: positive, negativeFeatures: negative };
  }, [features]);

  const totalPositive = positiveFeatures.reduce((sum, f) => sum + f.contribution, 0);
  const totalNegative = negativeFeatures.reduce((sum, f) => sum + f.contribution, 0);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
        SHAP Force Plot
      </Typography>
      
      <Box sx={{ display: 'flex', gap: 2, mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Base Value
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {baseValue.toFixed(3)}
          </Typography>
        </Box>
        <Box sx={{ mx: 2, borderLeft: '1px solid #374151' }} />
        <Box>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Final Prediction
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.primary', fontWeight: 600 }}>
            {finalPrediction.toFixed(3)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 4 }}>
        {/* Positive Forces */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ArrowUp size={20} className="text-green-400" />
            <Typography variant="subtitle2" sx={{ color: '#22C55E', fontWeight: 600 }}>
              Pushing Higher
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
              +{totalPositive.toFixed(3)}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {positiveFeatures.map((feature, index) => {
              const widthPercent = totalPositive !== 0 
                ? Math.abs(feature.contribution / totalPositive) * 100 
                : 0;
              return (
                <Box key={index}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      {feature.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#22C55E', fontSize: '0.75rem' }}>
                      +{feature.contribution.toFixed(3)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', height: 8, bgcolor: '#1F2937', borderRadius: 1, overflow: 'hidden' }}>
                    <Box 
                      sx={{ 
                        width: `${widthPercent}%`, 
                        height: '100%', 
                        bgcolor: '#22C55E',
                        transition: 'width 0.3s ease'
                      }} 
                    />
                  </Box>
                </Box>
              );
            })}
            {positiveFeatures.length === 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                No positive contributions
              </Typography>
            )}
          </Box>
        </Box>

        {/* Negative Forces */}
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <ArrowDown size={20} className="text-red-400" />
            <Typography variant="subtitle2" sx={{ color: '#EF4444', fontWeight: 600 }}>
              Pushing Lower
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
              {totalNegative.toFixed(3)}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {negativeFeatures.map((feature, index) => {
              const widthPercent = totalNegative !== 0 
                ? Math.abs(feature.contribution / totalNegative) * 100 
                : 0;
              return (
                <Box key={index}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                      {feature.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#EF4444', fontSize: '0.75rem' }}>
                      {feature.contribution.toFixed(3)}
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', height: 8, bgcolor: '#1F2937', borderRadius: 1, overflow: 'hidden' }}>
                    <Box 
                      sx={{ 
                        width: `${widthPercent}%`, 
                        height: '100%', 
                        bgcolor: '#EF4444',
                        transition: 'width 0.3s ease'
                      }} 
                    />
                  </Box>
                </Box>
              );
            })}
            {negativeFeatures.length === 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                No negative contributions
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={{ mt: 3, p: 2, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 1, border: '1px solid rgba(59, 130, 246, 0.2)' }}>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Net Effect: {positiveFeatures.length} features pushing higher, {negativeFeatures.length} features pushing lower
        </Typography>
      </Box>
    </Box>
  );
};

export default ForcePlot;
